"use client";

import { useState, useCallback, type FormEvent, type ChangeEvent } from "react";
import { signIn } from "../client";
import { getSafeAuthCallbackUrl } from "./callback-url";
import { DevCredentialsForm } from "./dev-credentials";

export function SignIn() {
  const [email, setEmail] = useState("");
  const [magicLinkState, setMagicLinkState] = useState<
    "idle" | "sending" | "sent"
  >("idle");

  const handleGoogleSignIn = useCallback(async () => {
    await signIn.social({
      provider: "google",
      callbackURL: getSafeAuthCallbackUrl(),
    });
  }, []);

  const handleMagicLinkSignIn = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMagicLinkState("sending");
    try {
      await signIn.magicLink({
        email: email.trim(),
        callbackURL: getSafeAuthCallbackUrl(),
      });
    } catch {
      // Keep the response generic so account existence and access are not disclosed.
    }
    setMagicLinkState("sent");
  }, [email]);

  const handleEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value), []);

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <button
        className="inline-flex h-11 w-full items-center justify-center gap-3 rounded-lg border border-border bg-background px-4 font-medium text-foreground shadow-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onClick={handleGoogleSignIn}
        type="button"
      >
        <svg
          aria-labelledby="google-icon-title"
          className="h-5 w-5"
          role="img"
          viewBox="0 0 24 24"
        >
          <title id="google-icon-title">Google logo</title>
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        Continue with Google
      </button>
      <form
        className="flex w-full flex-col gap-3"
        onSubmit={handleMagicLinkSignIn}
      >
        <label
          className="text-sm font-medium text-foreground"
          htmlFor="sign-in-email"
        >
          Or sign in with an email link
        </label>
        <input
          autoComplete="email"
          className="h-11 rounded-lg border border-border bg-background px-3 text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
          id="sign-in-email"
          onChange={handleEmailChange}
          placeholder="you@example.com"
          required
          type="email"
          value={email}
        />
        <button
          className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-primary px-4 font-medium text-primary-foreground transition-colors hover:opacity-90 disabled:opacity-60"
          disabled={magicLinkState === "sending"}
          type="submit"
        >
          {magicLinkState === "sending"
            ? "Sending…"
            : "Email me a sign-in link"}
        </button>
        {magicLinkState === "sent" && (
          <p aria-live="polite" className="text-sm text-muted-foreground">
            If an account exists for that email, a sign-in link is on its way.
          </p>
        )}
      </form>
      <DevCredentialsForm />
    </div>
  );
}
